import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import {
	getLenderLogoUrlByMortgage,
	MortgagePriorityDescription,
} from '@core/util/mortgage';
import {
	MatterMortgageType,
	TransactionType,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { Observable } from 'rxjs';

export interface MortgageTab {
	id: string;
	relatedMortgageId: string;
	type: MatterMortgageType;
	transactionType: TransactionType;
	description: string;
	logoUrl?: string;
}

export interface VM {
	tabs: MortgageTab[];
}

const defaultVM: VM = {
	tabs: [],
};

@UntilDestroy()
@Component({
	selector: 'app-mortgage-selector-tabs',
	templateUrl: './mortgage-selector-tabs.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MortgageSelectorTabsComponent
	extends BaseComponent<VM>
	implements OnInit
{
	@Input() canDelete = false;
	@Input() url = '';
	@Input() hideBridge = false;
	@Output() onSelected = new EventEmitter<string>();
	@Output() onDelete = new EventEmitter<MortgageTab>();

	public mortgageTabs: MortgageTab[] = [];

	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgageWithAssociations[]>;

	constructor() {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.mortgages$.pipe(untilDestroyed(this)).subscribe((mortgages) => {
			if (this.hideBridge) {
				mortgages = mortgages.filter(
					(m) => m.transactionType !== TransactionType.BRIDGE,
				);
			}
			const tabs = mortgages.map((_m) => ({
				id: _m.id,
				type: _m.type,
				relatedMortgageId: _m.relatedMortgageId,
				description: this.getMortgageTabName(_m),
				transactionType: _m.transactionType,
				url: this.getUrl(_m),
				logoUrl: getLenderLogoUrlByMortgage(_m),
			}));

			this.VM = {
				tabs,
			};
		});
	}

	public selectMortgage(id: string): void {
		this.onSelected.emit(id);
	}

	public deleteMortgage(tab: MortgageTab): void {
		this.onDelete.emit(tab);
	}

	getMortgageTabName(mortgage: MatterMortgageWithAssociations): string {
		if (mortgage?.transactionType === TransactionType.BRIDGE) {
			return '[Bridge]';
		}
		return MortgagePriorityDescription[mortgage?.priority];
	}

	getUrl(mortgage: MatterMortgageWithAssociations): string {
		return `${this.url}/${mortgage.id}`;
	}
}
